import { useState } from "react";
import type { DeviceEvent, DeviceEventType } from "../../api/types";
import { DEVICE_EVENT_TYPES } from "../../api/types";
import { useDeviceEvents } from "../../hooks/queries";
import {
  AlertStateBadge,
  SeverityBadge,
} from "../../components/domain/Badges";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Select } from "../../components/ui/Input";
import { Pagination } from "../../components/ui/Pagination";
import { Skeleton } from "../../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../../components/ui/States";
import { Badge, type BadgeTone } from "../../components/ui/Badge";
import { cn } from "../../lib/utils";
import { formatDateTime, formatRelative } from "../../lib/format";

const typeLabels: Record<DeviceEventType, string> = {
  alert: "Uyarı",
  patch: "Yama",
  inventory: "Envanter",
  enrollment: "Kayıt",
  status: "Durum",
};

const typeTones: Record<DeviceEventType, BadgeTone> = {
  alert: "danger",
  patch: "info",
  inventory: "muted",
  enrollment: "success",
  status: "warning",
};

const typeDotStyles: Record<DeviceEventType, string> = {
  alert: "bg-danger",
  patch: "bg-info",
  inventory: "bg-muted-foreground",
  enrollment: "bg-success",
  status: "bg-warning",
};

const typeFilterOptions = [
  { value: "", label: "Tümü" },
  ...DEVICE_EVENT_TYPES.map((type) => ({ value: type, label: typeLabels[type] })),
];

function EventEntry({ event }: { event: DeviceEvent }) {
  return (
    <li className="relative">
      <span
        aria-hidden="true"
        className={cn(
          "absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-background",
          typeDotStyles[event.type],
        )}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={typeTones[event.type]}>{typeLabels[event.type]}</Badge>
        <span className="text-sm font-medium">{event.message}</span>
        {event.severity ? <SeverityBadge severity={event.severity} /> : null}
        {event.type === "alert" ? <AlertStateBadge state="firing" /> : null}
      </div>
      <p className="text-muted-foreground mt-0.5 text-xs">
        {formatDateTime(event.at)} · {formatRelative(event.at)}
        {event.refId ? (
          <span className="font-mono"> · ref: {event.refId.slice(0, 8)}</span>
        ) : null}
      </p>
    </li>
  );
}

/**
 * Cihaz olay akışı — GET /devices/{id}/events ucundan beslenir
 * (uyarı, yama, envanter, kayıt ve durum olayları birleşik zaman çizelgesi).
 */
export function DeviceEventsTab({ deviceId }: { deviceId: string }) {
  const [type, setType] = useState<DeviceEventType | "">("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const query = useDeviceEvents(deviceId, {
    type: type || undefined,
    page,
    perPage,
  });

  return (
    <Card>
      <CardHeader className="flex-row items-end justify-between gap-4">
        <div>
          <CardTitle>Olay akışı</CardTitle>
          <CardDescription>
            Uyarı, yama, envanter, kayıt ve durum olayları (yeniden eskiye)
          </CardDescription>
        </div>
        <div className="w-48">
          <Select
            label="Olay türü"
            options={typeFilterOptions}
            value={type}
            onChange={(event) => {
              setType(event.target.value as DeviceEventType | "");
              setPage(1);
            }}
          />
        </div>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-64" />
        ) : query.isError ? (
          <ErrorState
            message="Olaylar yüklenemedi."
            onRetry={() => void query.refetch()}
          />
        ) : (query.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Olay yok"
            description="Bu cihaz için seçilen ölçüte uyan olay bulunamadı."
          />
        ) : (
          <ol className="border-border relative flex flex-col gap-4 border-l pl-6">
            {query.data?.items.map((event) => (
              <EventEntry key={event.id} event={event} />
            ))}
          </ol>
        )}

        {query.data ? (
          <div className="mt-4">
            <Pagination
              page={query.data.pageMeta.page}
              perPage={query.data.pageMeta.perPage}
              total={query.data.pageMeta.total}
              onPageChange={setPage}
              onPerPageChange={(next) => {
                setPerPage(next);
                setPage(1);
              }}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}